/* eslint-disable menti-react/filename-convention--jsx */

import { trackUser } from '@api/tracking/client';
import type { StaticContentEntity as StaticContentSchema } from '@core-api/presentation/types/response';
import { getLatestPresentationId } from '@mentimeter/presentation-state';
import * as React from 'react';
import { useLiveMomentStore, useQuestion } from '../../data-mediation';
import { useDispatch } from '../dispatcher';

type InteractiveContentWithStylingVisualization = Extract<
  StaticContentSchema,
  { interactiveContentStyling: { visualization: any } }
>['interactiveContentStyling'];

interface SlideActions {
  resetResults: (placement: 'Toolbar') => void;
  toggleCloseVoting: () => void;
  trackCloseVoting: (placement: 'Toolbar' | 'Hotkey C') => void;
  toggleVizUnits: () => void;
  toggleTrends: () => void;
  toggleJoinInstructions: () => void;
  setVisualization: (
    visualization: InteractiveContentWithStylingVisualization['visualization'],
  ) => void;
}

const defaultState: SlideActions = {
  resetResults: () => {},
  trackCloseVoting: () => {},
  toggleCloseVoting: () => {},
  toggleVizUnits: () => {},
  toggleTrends: () => {},
  toggleJoinInstructions: () => {},
  setVisualization: () => {},
};
const context = React.createContext<SlideActions>(defaultState);
context.displayName = 'SlideActionsContext';

const Provider = context.Provider;

export const SlideActionsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const dispatch = useDispatch();
  const showTrends = useLiveMomentStore((state) => state.showTrends);
  const isQrCodeSidebarVisible = useLiveMomentStore(
    (state) => state.isQrCodeSidebarVisible,
  );
  const setIsQrCodeSidebarVisible = useLiveMomentStore(
    (state) => state.setIsQrCodeSidebarVisible,
  );

  const { id, active, percent_default } = useQuestion();

  const resetResults = (placement: 'Toolbar') => {
    dispatch({
      type: 'RESET_SLIDE',
      payload: { id, placement },
    });
  };

  const trackCloseVoting = (placement: string) =>
    trackUser({
      event: 'Toggled close voting',
      properties: {
        context: 'Presentation view',
        mode: active ? 'Closed' : 'Opened',
        placement,
        'Presentation id': getLatestPresentationId(),
      },
    });

  const toggleCloseVoting = () => {
    dispatch({
      type: 'SET_QUESTION_SETTING',
      payload: {
        id,
        key: 'active',
        value: !active,
      },
    });
  };

  const toggleJoinInstructions = () => {
    setIsQrCodeSidebarVisible(!isQrCodeSidebarVisible);
  };

  const toggleVizUnits = () => {
    dispatch({
      type: 'SET_QUESTION_SETTING',
      payload: { id, key: 'percent_default', value: !percent_default },
    });
  };

  const setVisualization = (
    visualization:
      | InteractiveContentWithStylingVisualization['visualization']
      | undefined
      | null,
  ) => {
    const context = 'Presentation View';
    // TODO: Investigate tracking for visualization
    const tracking = visualization
      ? { layout: visualization, context }
      : { context };

    dispatch({
      type: 'SET_INTERACTIVE_CONTENT_VISAULIZATION',
      payload: { id, key: 'subType', value: visualization },
      meta: {
        tracking,
      },
    });
  };

  const toggleTrends = () => {
    dispatch({
      type: 'SET_PRESENTATION_SETTING',
      payload: { key: 'showTrends', value: !showTrends },
    });
  };

  return (
    <Provider
      value={{
        resetResults,
        trackCloseVoting,
        toggleCloseVoting,
        toggleVizUnits,
        toggleTrends,
        toggleJoinInstructions,
        setVisualization,
      }}
    >
      {children}
    </Provider>
  );
};

export const useSlideActions = () => React.useContext(context);

export const TestSlideActionsProvider = ({
  children,
  ...rest
}: React.PropsWithChildren<Partial<SlideActions>>) => {
  return <Provider value={{ ...defaultState, ...rest }}>{children}</Provider>;
};
